import { Facebook, Twitter, Linkedin, Instagram, FileVideo2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSearchVideoFolders, useVideoFolders } from '@/lib/api/hooks/video'
import { getVideoPreviewImage } from '@afsa/shared/utils'
import RecentArticles from '@/components/sections/RecentArticles'

import Search from '@/components/ui/Search'

import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'


export default function VideoFolders() {
    const navigate = useNavigate()

    // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    page,
    size,
    setSearch
  } = useSearchFilterParams()

    // Fetch video folders
    const { data: foldersResponse, isLoading: foldersLoading, isError: foldersError, refetch: refetchFolders } = useVideoFolders()
    
    // ── Search query (only active when mode === 'search') ─────────────────────
      const { data: searchData, isLoading: isSearchLoading, error: searchError, refetch: refetchSearch } = useSearchVideoFolders(
        searchTerm,
        { page, size }
    )

    const folders = mode === 'search' ? searchData?.data?.data || [] : foldersResponse?.data || []
    const error = mode === 'search' ? searchError : foldersError
    const refetchData = mode === 'search' ? refetchSearch : refetchFolders
    const loading = mode === 'search' ? isSearchLoading : foldersLoading


    const breadcrumbItems = [
        { label: 'Home', href: '/' },
        { label: 'Videos', href: '', active: true }
    ]


    const getPreviewImage = (previewUrls: string[] | undefined) => {
        if (previewUrls && previewUrls.length > 0) {
            const previewUrl = previewUrls[0]
            const image = getVideoPreviewImage(previewUrl)
            return image ? (
                <img
                    src={image}
                    alt="Video Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                        e.currentTarget.src = 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image'
                    }}
                />
            ) : <FileVideo2 className="w-24 h-24 text-gray-300" />
         }
            return <FileVideo2 className="w-24 h-24 text-gray-300" />

        }

        if (error) {
            return (
                <div className="text-center py-12 h-full flex flex-col items-center justify-center gap-4">
                    <p className="">Failed to load video folders. Please try again.</p>
                    <button
                        onClick={() => refetchData()}
                        className="px-4 py-2 bg-wine-100 text-white rounded hover:bg-wine-200 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            )
        }


    return (
        <div className="bg-white min-h-screen">
            {/* Breadcrumb */}
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-5">
                <Breadcrumb items={breadcrumbItems} />
            </div>

            {/* Main Content */}
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                <div className="flex flex-col lg:flex-row gap-12">
                    {/* Sidebar */}
                    <div className="w-full lg:w-96 flex flex-col gap-12">

                        {/* Recent Articles */}
                        <RecentArticles />

                        {/* Social Links */}
                        <div className="flex flex-col gap-4">
                            <h3 className="text-lg text-gray-500 font-['PT_Serif']">OUR SOCIALS</h3>
                            <div className="flex gap-2">
                                <button className="w-6 h-6 hover:opacity-70">
                                    <Facebook className="w-5 h-5 text-gray-800" />
                                </button>
                                <button className="w-6 h-6 hover:opacity-70">
                                    <Twitter className="w-5 h-5 text-gray-800" />
                                </button>
                                <button className="w-6 h-6 hover:opacity-70">
                                    <Instagram className="w-6 h-6 text-gray-800" />
                                </button>
                                <button className="w-6 h-6 hover:opacity-70">
                                    <Linkedin className="w-6 h-6 text-gray-800" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Folders Grid */}
                    <div className="flex-1 space-y-8">
                           {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search video folders..."
              />
            </div>
          </div>

                        <div className="flex flex-col gap-8">
                            {loading ? (
                                <LoadingSpinner message="Fetching results..." className="py-12" />
                            ) : folders.length === 0 ? (
                                <div className="text-center py-12">
                                    <p className="text-lg text-gray-500">{searchTerm ? "No video folders found matching your criteria." : "No video folders created."}</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
                                    {folders.map((folder) => (
                                        <div
                                            key={folder.folderId}
                                            onClick={() => navigate(`/media/videos/${folder.folderId}`)}
                                            className="shadow-lg flex flex-col hover:shadow-xl transition-shadow cursor-pointer"
                                        >
                                            <div className="w-full h-64 bg-gray-50 flex items-center justify-center  overflow-hidden relative">
                                               { getPreviewImage(folder.previewUrls)}
                                            </div>
                                            <div className="h-12 bg-white relative flex-shrink-0">
                                                <div className="absolute left-4 top-3 text-lg font-medium text-gray-900">
                                                    {folder.folderName}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
